// client/src/components/AdminHrDashboard.jsx
import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Paper, Grid, Card, CardContent, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Avatar, Button,
    TextField, InputAdornment, IconButton, Chip, Stack, LinearProgress,
    Divider, Tooltip, Zoom
} from '@mui/material';
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
    CartesianGrid, Tooltip as ChartTooltip, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
    Users, TrendingUp, ChevronRight, ArrowLeft, Search, Layers,
    FileSpreadsheet, CheckCircle2, AlertCircle, BarChart4, PieChart as PieIcon
} from 'lucide-react';

// Official Salesforce Lightning Design System (SLDS) Semantic Tokens
const SF_THEME = {
    textPrimary: '#181818',
    textSecondary: '#514f4d',
    borderStandard: '#dddbda',
    bgLight: '#fafaf9',
    bgCardHover: '#f3f2f1',
    accentBrand: '#0176d3',
    accentBrandLight: '#e0f2fe',
    successGreen: '#2e844a',
    successGreenLight: '#dcfce7',
    warningAmber: '#b78103',
    warningAmberLight: '#fef3c7',
    neutralGray: '#747474',
    chartColors: ['#0176d3', '#2e844a', '#b78103', '#747474']
};

const AdminHrDashboard = ({ leads = [], employeeMap = {}, onSync }) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [chartType, setChartType] = useState('composed'); // 'composed' | 'pie'

    // --- ENHANCED COMPUTATIONAL ENGINE ---
    const globalMetrics = useMemo(() => {
        const total = leads.length;
        let converted = 0;
        let working = 0;
        let pending = 0;

        leads.forEach(l => {
            const status = String(l?.status || '').toLowerCase().trim();
            if (status === 'converted') converted++;
            // Support alternate pipeline labels consistently
            else if (status === 'in progress' || status === 'working' || status === 'contacted') working++;
            else pending++;
        });

        const explicitPool = new Set();
        leads.forEach(l => {
            const rawId = l?.assigned_to || 'Unassigned';
            explicitPool.add(employeeMap[rawId] || rawId);
        });

        const conversionRate = total > 0 ? ((converted / total) * 100).toFixed(1) : '0.0';
        return { total, converted, working, pending, employeeCount: explicitPool.size, conversionRate };
    }, [leads, employeeMap]);

    // --- INDEPENDENT DATA DRILL-DOWN MATRIX PROCESSING ---
    const employeeBreakdown = useMemo(() => {
        const structuralMap = {};

        leads.forEach(lead => {
            if (!lead) return;
            const rawId = lead.assigned_to || 'Unassigned-Pool';
            const empName = employeeMap[rawId] || rawId;

            if (!structuralMap[empName]) {
                structuralMap[empName] = {
                    name: empName,
                    total: 0,
                    pending: 0,
                    working: 0,
                    converted: 0,
                    disqualified: 0,
                    records: []
                };
            }

            structuralMap[empName].total++;
            structuralMap[empName].records.push(lead);

            const cleanStatus = String(lead.status || '').toLowerCase().trim();
            if (cleanStatus === 'pending') {
                structuralMap[empName].pending++;
            } else if (cleanStatus === 'in progress' || cleanStatus === 'working' || cleanStatus === 'contacted') {
                structuralMap[empName].working++;
            } else if (cleanStatus === 'converted') {
                structuralMap[empName].converted++;
            } else {
                structuralMap[empName].disqualified++;
            }
        });

        const arrayForm = Object.values(structuralMap);
        if (!searchTerm) return arrayForm;

        const normalizedSearch = searchTerm.toLowerCase().trim();
        return arrayForm.filter(emp => emp.name.toLowerCase().includes(normalizedSearch));
    }, [leads, searchTerm, employeeMap]);

    // --- CHART MATRIX SYNC DATA ADAPTER ---
    const chartData = useMemo(() => {
        return employeeBreakdown.slice(0, 8).map(emp => ({
            name: emp.name.length > 12 ? `${emp.name.substring(0, 10)}...` : emp.name,
            'Total Assigned': emp.total,
            'Converted': emp.converted,
            'Conversion %': parseFloat((emp.total > 0 ? (emp.converted / emp.total) * 100 : 0).toFixed(1))
        }));
    }, [employeeBreakdown]);

    const pieChartData = useMemo(() => {
        return [
            { name: 'Converted Success', value: globalMetrics.converted },
            { name: 'Working Pipeline', value: globalMetrics.working },
            { name: 'Pending Allocation', value: globalMetrics.pending }
        ];
    }, [globalMetrics]);

    // --- VIEW CONTROLLER: INTERACTION DRILL DEEP LAYER ---
    if (selectedEmployee) {
        const targetedEmployeeData = employeeBreakdown.find(e => e.name === selectedEmployee) || {
            name: selectedEmployee, total: 0, converted: 0, working: 0, pending: 0, records: []
        };

        return (
            <Zoom in={true} style={{ transitionDelay: '50ms' }}>
                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3, bgcolor: '#ffffff', minHeight: '100vh' }}>

                    {/* Top Action Bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${SF_THEME.borderStandard}`, pb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowLeft size={14} />}
                                onClick={() => setSelectedEmployee(null)}
                                sx={{
                                    borderColor: SF_THEME.borderStandard,
                                    color: SF_THEME.textSecondary,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    '&:hover': { bgcolor: SF_THEME.bgLight, borderColor: SF_THEME.neutralGray }
                                }}
                            >
                                Back to Enterprise Analytics
                            </Button>
                            <Divider orientation="vertical" flexItem sx={{ height: 24 }} />
                            <Box>
                                <Typography sx={{ color: SF_THEME.textPrimary, fontSize: '18px', fontWeight: 700, tracking: '-0.02em' }}>
                                    Performance Trace Summary
                                </Typography>
                                <Typography variant="caption" sx={{ color: SF_THEME.textSecondary }}>
                                    Target Allocation Node: <b>{selectedEmployee}</b>
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Drill Down Dynamic Cards */}
                    <Grid container spacing={3}>
                        {[
                            { label: 'Total Allocated Assets', val: targetedEmployeeData.total, color: SF_THEME.textPrimary },
                            { label: 'Closed Converted Yield', val: targetedEmployeeData.converted, color: SF_THEME.successGreen },
                            { label: 'Pipeline Operating Capacity', val: targetedEmployeeData.working, color: SF_THEME.accentBrand },
                            { label: 'Net Efficiency Ratio', val: `${targetedEmployeeData.total ? ((targetedEmployeeData.converted / targetedEmployeeData.total) * 100).toFixed(1) : '0.0'}%`, color: SF_THEME.warningAmber }
                        ].map((card, idx) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                                <Paper sx={{ p: 2.5, border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px' }}>
                                    <Typography variant="caption" sx={{ color: SF_THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: card.color }}>{card.val}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Drilled Row Level Data Ledger */}
                    <Paper sx={{ border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px' }}>
                        <Box sx={{ p: 2, bgcolor: SF_THEME.bgLight, borderBottom: `1px solid ${SF_THEME.borderStandard}` }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: SF_THEME.textPrimary }}>
                                Isolated Candidate Records Trace
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#ffffff' }}>
                                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '11px', color: SF_THEME.textSecondary, textTransform: 'uppercase', py: 1.5 } }}>
                                        <TableCell>Lead Candidate Name</TableCell>
                                        <TableCell>Target Track Course</TableCell>
                                        <TableCell>College Anchor Campus</TableCell>
                                        <TableCell>Email Registry Route</TableCell>
                                        <TableCell align="right">Pipeline Stage Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {targetedEmployeeData.records.map((record, index) => (
                                        <TableRow key={record.id || index} hover sx={{ '& td': { py: 1.5 } }}>
                                            <TableCell sx={{ fontWeight: 700, color: SF_THEME.accentBrand }}>{record.student_name || record.name || '—'}</TableCell>
                                            <TableCell sx={{ color: SF_THEME.textPrimary }}>{record.course_interest || record.course || '—'}</TableCell>
                                            <TableCell sx={{ color: SF_THEME.textSecondary }}>{record.college || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{record.email || record.email_id || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={record.status || 'Pending'}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                        bgcolor: String(record.status).toLowerCase() === 'converted' ? SF_THEME.successGreen : String(record.status).toLowerCase() === 'in progress' ? SF_THEME.accentBrand : SF_THEME.neutralGray,
                                                        color: '#ffffff',
                                                        borderRadius: '3px'
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {targetedEmployeeData.records.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 6, color: SF_THEME.textSecondary }}>
                                                No active operational records bound to this personnel instance profile.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </Zoom>
        );
    }

    // --- MAIN DASHBOARD LAYOUT ENGINE ---
    return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4, bgcolor: '#fbfbfb', minHeight: '100vh', boxSizing: 'border-box' }}>

            {/* Top Cards Grid */}
            <Grid container spacing={3}>
                {[
                    { title: 'Cross-Sheet Active Ledger', val: globalMetrics.total, sub: `${globalMetrics.employeeCount} Active Assignment Profiles`, icon: <FileSpreadsheet size={18} />, color: SF_THEME.accentBrand, bg: SF_THEME.accentBrandLight },
                    { title: 'Closed Converted Success', val: globalMetrics.converted, sub: 'Pipeline items complete', icon: <CheckCircle2 size={18} />, color: SF_THEME.successGreen, bg: SF_THEME.successGreenLight, progress: parseFloat(globalMetrics.conversionRate) },
                    { title: 'Macro Conversion Yield', val: `${globalMetrics.conversionRate}%`, sub: 'Average aggregate conversion ratio', icon: <TrendingUp size={18} />, color: SF_THEME.warningAmber, bg: SF_THEME.warningAmberLight },
                    { title: 'Pipeline Operation Load', val: globalMetrics.working, sub: "Marked 'In-Progress'", icon: <Layers size={18} />, color: SF_THEME.neutralGray, bg: '#f3f2f1' }
                ].map((m, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                        <Card sx={{ border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px', bgcolor: '#ffffff', height: '100%' }}>
                            <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box', '&:last-child': { pb: 2.5 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: SF_THEME.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.title}</Typography>
                                        <Typography sx={{ fontSize: '28px', fontWeight: 800, mt: 0.5, color: m.color === SF_THEME.accentBrand ? SF_THEME.textPrimary : m.color }}>{m.val}</Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: m.bg, color: m.color, width: 38, height: 38, flexShrink: 0 }}>{m.icon}</Avatar>
                                </Stack>
                                {m.progress !== undefined ? (
                                    <Box sx={{ mt: 2, width: '100%' }}>
                                        <LinearProgress variant="determinate" value={m.progress} sx={{ height: 4, borderRadius: 2, bgcolor: '#e9e9e9', '& .MuiLinearProgress-bar': { bgcolor: SF_THEME.successGreen } }} />
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '11px', color: SF_THEME.textSecondary, mt: 1.5 }}>{m.sub}</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Visual Analytics Split Section */}
            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} lg={7} sx={{ display: 'flex' }}>
                    <Paper sx={{ p: 3, border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px', bgcolor: '#ffffff', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} spacing={2}>
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: SF_THEME.textPrimary }}>Workforce Production Performance Engine</Typography>
                                <Typography variant="caption" sx={{ color: SF_THEME.textSecondary }}>Statistical comparative tracking of primary operators.</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} sx={{ border: `1px solid ${SF_THEME.borderStandard}`, p: 0.5, borderRadius: '4px', bgcolor: SF_THEME.bgLight, flexShrink: 0 }}>
                                <IconButton size="small" onClick={() => setChartType('composed')} sx={{ borderRadius: '3px', bgcolor: chartType === 'composed' ? '#ffffff' : 'transparent', boxShadow: chartType === 'composed' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none' }}>
                                    <BarChart4 size={14} color={chartType === 'composed' ? SF_THEME.accentBrand : SF_THEME.textSecondary} />
                                </IconButton>
                                <IconButton size="small" onClick={() => setChartType('pie')} sx={{ borderRadius: '3px', bgcolor: chartType === 'pie' ? '#ffffff' : 'transparent', boxShadow: chartType === 'pie' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none' }}>
                                    <PieIcon size={14} color={chartType === 'pie' ? SF_THEME.accentBrand : SF_THEME.textSecondary} />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Box sx={{ width: '100%', height: 260 }}>
                            {chartType === 'composed' ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ top: 5, right: -10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f2f1" vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: SF_THEME.textSecondary }} stroke={SF_THEME.borderStandard} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: SF_THEME.textSecondary }} stroke={SF_THEME.borderStandard} />
                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: SF_THEME.textSecondary }} stroke={SF_THEME.borderStandard} />
                                        <ChartTooltip contentStyle={{ fontSize: '11px', borderRadius: '4px', border: `1px solid ${SF_THEME.borderStandard}` }} />
                                        <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                                        <Bar yAxisId="left" dataKey="Total Assigned" fill="#e0f2fe" stroke="#0176d3" strokeWidth={1} barSize={24} name="Assigned Leads" />
                                        <Bar yAxisId="left" dataKey="Converted" fill="#2e844a" barSize={16} name="Converted Success" />
                                        <Line yAxisId="right" type="monotone" dataKey="Conversion %" stroke="#b78103" strokeWidth={2} dot={{ r: 3 }} name="Yield efficiency %" />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                                            {pieChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={SF_THEME.chartColors[index % SF_THEME.chartColors.length]} />
                                            ))}
                                        </Pie>
                                        <ChartTooltip contentStyle={{ fontSize: '11px' }} />
                                        <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: '11px' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={5} sx={{ display: 'flex' }}>
                    <Paper sx={{ p: 3, border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px', bgcolor: '#ffffff', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: SF_THEME.textPrimary }}>System Integrity Diagnostic Matrix</Typography>
                            <Typography variant="caption" sx={{ color: SF_THEME.textSecondary }}>Validation metrics across dynamic sheet runtime pipelines.</Typography>
                            <Divider sx={{ my: 2, borderColor: SF_THEME.borderStandard }} />

                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <AlertCircle size={16} color={SF_THEME.accentBrand} />
                                        <Typography variant="body2" sx={{ color: SF_THEME.textSecondary, fontWeight: 500 }}>Unassigned Orphan Pools</Typography>
                                    </Stack>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{employeeBreakdown.find(e => e.name.toLowerCase().includes('unassigned'))?.total || 0} Units</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <CheckCircle2 size={16} color={SF_THEME.successGreen} />
                                        <Typography variant="body2" sx={{ color: SF_THEME.textSecondary, fontWeight: 500 }}>Global Operation Convergence Ratio</Typography>
                                    </Stack>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: SF_THEME.successGreen }}>Verified Active</Typography>
                                </Box>
                            </Stack>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: SF_THEME.bgLight, borderRadius: '4px', border: `1px solid ${SF_THEME.borderStandard}`, mt: 2 }}>
                            <Typography variant="caption" sx={{ color: SF_THEME.textSecondary, display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase' }}>Operational Infrastructure Note</Typography>
                            <Typography variant="caption" sx={{ color: SF_THEME.textSecondary, lineHeight: '1.4em' }}>
                                To examine individual runtime execution details or isolate candidate leads, click an entry directly inside the Employee Ledger Allocation Table below.
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Corporate Employee Primary Ledger */}
            <Paper sx={{ border: `1px solid ${SF_THEME.borderStandard}`, boxShadow: 'none', borderRadius: '4px', overflow: 'hidden', bgcolor: '#ffffff' }}>
                <Box sx={{ p: 2, bgcolor: SF_THEME.bgLight, borderBottom: `1px solid ${SF_THEME.borderStandard}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Users size={16} color={SF_THEME.textSecondary} />
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: SF_THEME.textPrimary }}>
                            Employee Ledger Allocation Metrics
                        </Typography>
                    </Stack>
                    <TextField
                        size="small"
                        placeholder="Filter ledger data rows..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: '260px' }}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><Search size={13} /></InputAdornment>),
                            sx: { fontSize: '12px', bgcolor: '#ffffff', height: '32px' }
                        }}
                    />
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#ffffff' }}>
                            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '11px', color: SF_THEME.textSecondary, py: 1.5, borderBottom: `2px solid ${SF_THEME.borderStandard}` } }}>
                                <TableCell>Personnel Owner Name</TableCell>
                                <TableCell align="center">Total Leads Loaded</TableCell>
                                <TableCell align="center">Unassigned / Pending</TableCell>
                                <TableCell align="center">Working / Contacted</TableCell>
                                <TableCell align="center">Closed Converted Success</TableCell>
                                <TableCell align="right" sx={{ pr: 3 }}>Drill-Down Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employeeBreakdown.map((row) => (
                                <TableRow
                                    key={row.name}
                                    hover
                                    onClick={() => setSelectedEmployee(row.name)}
                                    sx={{ cursor: 'pointer', '& td': { py: 1.2 } }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ width: 26, height: 26, fontSize: '10px', fontWeight: 700, bgcolor: SF_THEME.accentBrand }}>
                                                {row.name.substring(0, 2).toUpperCase()}
                                            </Avatar>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: SF_THEME.accentBrand, '&:hover': { textDecoration: 'underline' } }}>
                                                {row.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: SF_THEME.textPrimary }}>{row.total}</TableCell>
                                    <TableCell align="center"><Chip size="small" label={row.pending} sx={{ height: '18px', fontSize: '11px', fontWeight: 600, bgcolor: SF_THEME.warningAmberLight, color: SF_THEME.warningAmber, borderRadius: '3px' }} /></TableCell>
                                    <TableCell align="center"><Chip size="small" label={row.working} sx={{ height: '18px', fontSize: '11px', fontWeight: 600, bgcolor: SF_THEME.accentBrandLight, color: SF_THEME.accentBrand, borderRadius: '3px' }} /></TableCell>
                                    <TableCell align="center"><Chip size="small" label={row.converted} sx={{ height: '18px', fontSize: '11px', fontWeight: 700, bgcolor: SF_THEME.successGreenLight, color: SF_THEME.successGreen, borderRadius: '3px' }} /></TableCell>
                                    <TableCell align="right" sx={{ pr: 2 }}>
                                        <Tooltip title="Open Drill-down Profile Engine" arrow placement="left">
                                            <IconButton size="small" sx={{ color: SF_THEME.accentBrand }}><ChevronRight size={16} /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {employeeBreakdown.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: SF_THEME.textSecondary }}>
                                        No corresponding records parsed inside current sheet configurations.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default AdminHrDashboard;